import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  Paper,
  useTheme
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import Footer from '../components/Footer';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { login } = useAuth();
  const theme = useTheme();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check for empty fields
    if (!email || !password) {
      setError('Email and password are required');
      return;
    }

    // Email format validation
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    try {
      const user = await login(email, password);
      if (user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to login');
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: theme.palette.mode === 'dark' ? 'background.default' : '#f8f9fa'
    }}>
      {/* Header */}
      <Box sx={{ 
        bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white', 
        borderBottom: `1px solid ${theme.palette.mode === 'dark' ? '#333' : '#e0e0e0'}`,
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        mb: 4
      }}>
        <Container maxWidth="lg">
          <Box sx={{ 
            py: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            cursor: 'pointer'
          }} onClick={() => navigate('/')}>
            <img 
              src="/favicon.ico" 
              alt="Glowspirit" 
              style={{ height: 32, width: 32 }}
            />
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 600, 
                color: theme.palette.mode === 'dark' ? '#fff' : '#1a237e'
              }}
            >
              Glowspirit
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Container component="main" maxWidth="xs" sx={{ flex: 1 }}>
        <Paper 
          elevation={0}
          sx={{ 
            p: 4, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}
        >
          <Typography component="h1" variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
            Welcome Back
          </Typography>
          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ 
                mt: 3, 
                mb: 2,
                py: 1.5,
                bgcolor: theme.palette.mode === 'dark' ? 'primary.dark' : '#1a237e',
                '&:hover': {
                  bgcolor: theme.palette.mode === 'dark' ? 'primary.main' : '#000051'
                }
              }}
            >
              Sign In
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Link 
                href="/register" 
                variant="body2"
                sx={{ 
                  color: theme.palette.mode === 'dark' ? 'primary.main' : '#1a237e',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                Don't have an account? Sign Up
              </Link>
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* Footer */}
      <Footer />
    </Box>
  );
};

export default Login; 